import { useCallback } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export type OnboardingEventType =
  | "first_login"
  | "auto_redirect"
  | "quick_start_viewed"
  | "path_selection_started"
  | "path_selected"
  | "board_created"
  | "first_project_created"
  | "first_session_started";

interface OnboardingEventData {
  path?: "prebuilt" | "custom" | "braindump";
  boardId?: string;
  projectId?: string;
  sessionId?: string;
  [key: string]: any;
}

export const useOnboardingTracking = () => {
  const { user, session } = useAuth();

  const trackEvent = useCallback(
    async (eventType: OnboardingEventType, eventData: OnboardingEventData = {}) => {
      if (!user?.id) {
        console.log('[OnboardingTracking] No user, skipping tracking for:', eventType);
        return;
      }

      try {
        // Verify user is still authenticated before tracking
        if (!session) {
          console.warn('[OnboardingTracking] No active session, skipping tracking for:', eventType);
          return;
        }

        // Check if this event has already been tracked (for "first_*" events)
        if (
          eventType === "first_login" ||
          eventType === "first_project_created" ||
          eventType === "first_session_started"
        ) {
          const { data: existingEvents } = await supabase
            .from("user_onboarding_events")
            .select("id")
            .eq("user_id", user.id)
            .eq("event_type", eventType)
            .limit(1);

          if (existingEvents && existingEvents.length > 0) {
            // Event already tracked, skip
            return;
          }
        }

        const { error } = await supabase
          .from("user_onboarding_events")
          .insert({
            user_id: user.id,
            event_type: eventType,
            event_data: eventData,
          });

        if (error) {
          console.error(`Error tracking ${eventType}:`, error);
        }
      } catch (error) {
        console.error(`Error tracking ${eventType}:`, error);
      }
    },
    [user?.id, session]
  );

  return { trackEvent };
};
